import { type FieldValues, type Path, type UseFormReturn } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';

interface FormDatePickerProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
}

export function FormDatePicker<T extends FieldValues>({
  form,
  name,
  label,
  className,
  disabled,
  required,
}: FormDatePickerProps<T>) {
  const error = form.formState.errors[name];

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={name}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <Input
        id={name}
        type="date"
        disabled={disabled}
        // `color-scheme: dark` makes the browser render the native calendar
        // icon in light strokes against the dark input background.
        className="cursor-pointer dark:[color-scheme:dark]"
        {...form.register(name)}
        // Open the date picker on any click in the input — not just the icon.
        // showPicker() lands in 2022+ browsers; older Safari is harmless no-op.
        onClick={(e) => {
          try { (e.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch { /* unsupported / disabled */ }
        }}
      />
      {error && (
        <p className="text-xs text-destructive">{error.message as string}</p>
      )}
    </div>
  );
}
